// TermsOfUsePage.js
import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Divider,
  Link,
  useColorModeValue,
  Button,
  Select
} from "@chakra-ui/react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";


const TermsOfUsePage = () => {
  const { t, i18n } = useTranslation();

  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const linkColor = useColorModeValue("blue.500", "blue.300");

  const navigate = useNavigate();

    // Handle language change
    const handleLanguageChange = (event) => {
      i18n.changeLanguage(event.target.value);
    };

  return (
    <Box bg={useColorModeValue("gray.50", "gray.900")} minH="100vh" py={10}>
            {/* Language Switcher */}
            <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
              <Select
                value={i18n.language}
                onChange={handleLanguageChange}
                width="120px"
                bg="white"
                color="black"
                size="sm"
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="es">Español</option>
              </Select>
            </Box>
      <Container
        maxW="container.md"
        bg={useColorModeValue("white", "gray.800")}
        p={8}
        borderRadius="lg"
        boxShadow="xl"
      >
        <VStack spacing={6} align="stretch">
          {/* Title */}
          <Heading size="2xl" color={headingColor} textAlign="center">
            {t("termsofusepage.title")}
          </Heading>

          {/* Effective Date */}
          <Text fontSize="md" color={textColor} textAlign="center">
            {t("termsofusepage.effective_date")}
          </Text>
          <Divider />

          {/* Intro */}
          <Text fontSize="md" color={textColor}>
            {t("termsofusepage.intro.welcome")}{" "}
            <Link as={NavLink} to="/privacy-policy" color={linkColor}>
              {t("termsofusepage.intro.privacy_policy_link")}
            </Link>
          </Text>
          <Divider />

          {/* 1. Definitions */}
          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.definitions.title")}
          </Heading>

          <Text color={textColor}>
            <strong>{t("termsofusepage.definitions.peerx.title")}</strong>{" "}
            {t("termsofusepage.definitions.peerx.description")}
          </Text>

          <Text color={textColor}>
            <strong>{t("termsofusepage.definitions.user.title")}</strong>{" "}
            {t("termsofusepage.definitions.user.description")}
          </Text>

          <Text color={textColor}>
            <strong>{t("termsofusepage.definitions.listing.title")}</strong>{" "}
            {t("termsofusepage.definitions.listing.description")}
          </Text>

          <Text color={textColor}>
            <strong>{t("termsofusepage.definitions.transaction.title")}</strong>{" "}
            {t("termsofusepage.definitions.transaction.description")}
          </Text>

          <Text color={textColor}>
            {t("termsofusepage.definitions.conclusion")}
          </Text>

          {/* 2. Eligibility */}
          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.eligibility.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.eligibility.age_requirement")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.eligibility.legal_authority")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.eligibility.accurate_information")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.eligibility.compliance")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.eligibility.responsibility")}
          </Text>

          {/* 3. Account Registration & Security */}
          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.account_registration.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.account_registration.registration_requirement")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_registration.account_security")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_registration.security_measures")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_registration.suspension_rights")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_registration.security_commitment")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.services_provided.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.services_provided.platform_description")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.services_provided.intermediary_role")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.services_provided.listing_control")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.services_provided.platform_improvements")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.services_provided.user_responsibility")}
          </Text>

          <Divider />

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.transactions.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.transactions.user_transactions")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.transactions.communication")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.transactions.compliance")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.transactions.secure_payments")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.transactions.responsibility")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.user_responsibilities.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.user_responsibilities.integrity")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.user_responsibilities.misleading_listings")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.user_responsibilities.security")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.user_responsibilities.intellectual_property")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.user_responsibilities.responsibility")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.fees_payments.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.fees_payments.fees_structure")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.fees_payments.sufficient_funds")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.fees_payments.adjust_fees")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.fees_payments.secure_payments")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.fees_payments.dispute_resolution")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.dispute_resolution.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.dispute_resolution.encouragement")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.dispute_resolution.accuracy_requirement")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.dispute_resolution.arbitration_agreement")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.dispute_resolution.liability_disclaimer")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.dispute_resolution.policy_updates")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.account_termination.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.account_termination.policy")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_termination.user_closure")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_termination.liability_disclaimer")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_termination.violation_consequences")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.account_termination.responsibility")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.governing_law.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.governing_law.applicable_laws")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.governing_law.jurisdiction")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.governing_law.compliance")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.governing_law.legal_disputes")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.governing_law.conflicts")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.changes_to_terms.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.changes_to_terms.modifications")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.changes_to_terms.notifications")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.changes_to_terms.user_responsibility")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.changes_to_terms.explicit_consent")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.changes_to_terms.feedback")}
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("termsofusepage.contact_information.title")}
          </Heading>
          <Text color={textColor}>
            {t("termsofusepage.contact_information.intro")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.contact_information.email_label")}
          </Text>
          <Text color={textColor}>
            📧 <Link href="mailto:privacy@peerx.ca" color={linkColor}>{t("termsofusepage.contact_information.email")}</Link>
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.contact_information.formal_correspondence")}
          </Text>
          <Text color={textColor}>
            📍 {t("termsofusepage.contact_information.address")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.contact_information.support_hours")}
          </Text>
          <Text color={textColor}>
            {t("termsofusepage.contact_information.user_suggestions")}
          </Text>

          <Button
            colorScheme="blue"
            variant="solid"
            onClick={() => navigate("/")}>
            {t("termsofusepage.back_to_home")}
          </Button>

          <Text color={textColor}>
            {t("termsofusepage.thank_you")}
          </Text>

        </VStack>
      </Container>
    </Box>
  );
};

export default TermsOfUsePage;
